<template>
  <a-modal
    v-model:visible="modalVisible"
    cancel-text="关闭"
    :okButtonProps="{ style: { display: 'none' } }"
    class="modal-patientFile modal-patientPortrait"
    width="95%"
    style="top: 40px"
    :maskClosable="false"
    :destroyOnClose="true"
    wrapClassName="patient-portrait-modal-wrap"
  >
    <template #title>
      <div class="portrait-modal-title">
        <div class="portrait-modal-title__meta">
          <span class="portrait-modal-title__text">{{ title }}</span>
          <span v-if="updateTimeLabel" class="portrait-modal-title__time">
            {{ updateTimeLabel }}
          </span>
        </div>
        <div class="portrait-modal-title__refresh" @click="onRefresh">
          <ReloadOutlined class="portrait-modal-title__refresh-icon" :spin="refreshing" />
          <span>{{ isFirstGenerate ? '生成' : '刷新' }}</span>
        </div>
      </div>
    </template>
    <div class="patient-portrait-modal">
      <div class="HeaderInfo">
        <HeaderInfo :patient-data="portraitData" />
      </div>
      <a-spin :spinning="loading">
        <a-skeleton v-if="loading && !initialized" active :paragraph="{ rows: 10 }" />
        <div v-else class="content-grid">
          <div class="left-column">
            <SummaryPanel :summary="summaryData" />
            <TimelinePanel
              :ucDoctorId="ucDoctorId"
              :ucPatientId="ucPatientId"
              :self-reported-feedbacks="selfReportedFeedbacks"
              :self-report-new="selfReportedFeedbacksNew"
            />
          </div>

          <div class="portrait-panel">
            <div class="portrait-panel__head">
              <span class="portrait-panel__title">患者画像</span>
              <a-button type="primary" :disabled="!canEdit" @click="onEditFigure">
                编辑画像
              </a-button>
            </div>
            <div class="portrait-panel__body">
              <div class="portrait-panel__figure">
                <img
                  v-if="portraitImageSrc"
                  :src="portraitImageSrc"
                  alt="患者画像"
                  class="portrait-panel__figure-image"
                />
              </div>
              <div class="portrait-panel__side">
                <InsightCard title="当前状态" :items="stateItems" />
                <InsightCard title="沟通与行为" :items="communicationItems" />
                <InsightCard
                  title="医生诊断"
                  :items="diagnosisItems"
                  :medicines="diagnosisMedicines"
                  :historical-medications="historicalMedications"
                  show-historical-medications
                  :current-medication-new="currentMedicationNew"
                  :historical-medications-new="historicalMedicationsNew"
                />
              </div>
            </div>
          </div>
        </div>
      </a-spin>
    </div>
    <EditPortraitModal
      v-model:visible="editModalVisible"
      :initial-data="portraitData"
      :portrait-dict="portraitDict"
      @save="onSavePortrait"
    />
    <ComplaintRiskModal
      v-model:visible="complaintModalVisible"
      :tip-config="complaintTipConfig"
      :records="complaintRecords"
    />
  </a-modal>
</template>

<script setup>
import { computed, defineEmits, defineExpose, defineProps, ref } from 'vue'
import { ReloadOutlined } from '@ant-design/icons-vue'
import moment from 'moment'
import HeaderInfo from './components/HeaderInfo.vue'
import SummaryPanel from './components/SummaryPanel.vue'
import TimelinePanel from './components/TimelinePanel.vue'
import InsightCard from './components/InsightCard.vue'
import EditPortraitModal from './components/EditPortraitModal.vue'
import ComplaintRiskModal from './components/ComplaintRiskModal.vue'
import { usePortraitData } from './hooks/usePortraitData'
import { createPortraitViewModel } from './model/portraitView'
import { formatMedicationEndTime } from '@/utils'

const DEFAULT_PORTRAIT_IMAGE_SRC = 'https://file.drstrong.cn/frontend/bd-work-bench/未知.png'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  ucDoctorId: {
    type: [String, Number],
    default: ''
  },
  ucPatientId: {
    type: [String, Number],
    default: ''
  },
  title: {
    type: String,
    default: '患者档案'
  },
  fetcher: {
    type: Function,
    default: null
  }
})

const emit = defineEmits(['update:visible', 'edit', 'viewDetail'])

const modalVisible = computed({
  get: () => props.visible,
  set: value => emit('update:visible', value)
})

const {
  loading,
  initialized,
  portraitData,
  portraitDict,
  refreshing,
  remainSeconds,
  refresh,
  reload
} = usePortraitData({
  visible: modalVisible,
  ucDoctorId: computed(() => props.ucDoctorId),
  ucPatientId: computed(() => props.ucPatientId),
  fetcher: computed(() => props.fetcher)
})

const portraitViewModel = computed(() =>
  createPortraitViewModel(portraitData.value, { formatMedicationEndTime })
)

const summaryData = computed(() => portraitViewModel.value.summary)
const stateItems = computed(() => portraitViewModel.value.stateItems)

const complaintModalVisible = ref(false)
const complaintTipConfig = computed(() => portraitViewModel.value.complaintRisk.tipConfig)
const complaintRecords = computed(() => portraitViewModel.value.complaintRisk.records)

// 沟通与行为条目：末尾追加投诉风险入口，无记录时仅展示风险文字
const communicationItems = computed(() => {
  const risk = portraitViewModel.value.complaintRisk
  return [
    ...portraitViewModel.value.communicationItems,
    {
      label: '投诉风险',
      value: risk.riskLabel,
      valueColor: risk.riskColor,
      clickable: risk.clickable,
      suffixIcon: risk.clickable,
      isNew: risk.isNew,
      onClick: risk.clickable ? () => (complaintModalVisible.value = true) : undefined
    }
  ]
})
const diagnosisItems = computed(() => portraitViewModel.value.diagnosisItems)
const diagnosisMedicines = computed(() => portraitViewModel.value.diagnosisMedicines)
const historicalMedications = computed(() => portraitViewModel.value.historicalMedications)
const selfReportedFeedbacks = computed(() => portraitViewModel.value.selfReportedFeedbacks)
const currentMedicationNew = computed(() => portraitViewModel.value.currentMedicationNew)
const historicalMedicationsNew = computed(() => portraitViewModel.value.historicalMedicationsNew)
const selfReportedFeedbacksNew = computed(() => portraitViewModel.value.selfReportedFeedbacksNew)
const canEdit = computed(() => portraitData.value?.canEdit === true)

const isFirstGenerate = computed(() => !portraitData.value?.profileUpdatedAt)

const portraitImageSrc = computed(
  () =>
    (portraitData.value.patientPortraitBackground || DEFAULT_PORTRAIT_IMAGE_SRC) +
    `?t=${Date.now()}`
)

const editModalVisible = ref(false)

const updateTimeLabel = computed(() => {
  if (refreshing.value) {
    return `${isFirstGenerate.value ? '生成' : '更新'}中，预计需要${remainSeconds.value}秒...`
  }
  const updateTime = portraitData.value?.profileUpdatedAt
  const date = updateTime ? moment(updateTime) : null
  return date?.isValid() ? `更新于 ${date.format('YYYY-MM-DD HH:mm:ss')}` : ''
})

const onRefresh = () => {
  // 提交任务、轮询失败的提示已在 usePortraitData 内统一处理
  refresh()
}

const onEditFigure = () => {
  if (!canEdit.value) return
  editModalVisible.value = true
  emit('edit', portraitData.value)
}

// 编辑成功后不在本地拼装详情，直接回源刷新，保证展示和服务端一致。
const onSavePortrait = async () => {
  await reload()
}

defineExpose({
  reload
})
</script>

<style lang="scss">
.patient-portrait-modal-wrap .modal-patientPortrait .ant-modal-header {
  height: 56px !important;
  padding: 0 24px !important;
  display: flex;
  align-items: center;
}

.patient-portrait-modal-wrap .modal-patientPortrait .ant-modal-title {
  height: 56px !important;
  line-height: 56px !important;
  display: flex;
  align-items: center;
}

.patient-portrait-modal-wrap .modal-patientPortrait .ant-modal-close-x {
  width: 56px !important;
  height: 56px !important;
  line-height: 56px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

// 标题栏：标题 + 更新时间 + 刷新按钮（样式按 UI 稿 Frame 134）
.patient-portrait-modal-wrap .modal-patientPortrait .portrait-modal-title {
  display: flex;
  align-items: center;
  gap: 8px;

  &__meta {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__text {
    color: rgba(0, 0, 0, 0.85);
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
  }

  &__time {
    color: #666666;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
  }

  &__refresh {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #1890ff;
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    cursor: pointer;
    user-select: none;

    &:hover {
      opacity: 0.85;
    }

    &-icon {
      font-size: 14px;
    }
  }
}
</style>

<style lang="scss" scoped>
.patient-portrait-modal {
  background: #f3f3f3;
  padding: 0 24px 12px;
  overflow: hidden auto;
  height: 80vh;
  width: 100%;

  .HeaderInfo {
    margin-top: 1px;
  }

  .content-grid {
    margin-top: 12px;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: flex-start;
    min-height: 650px;
  }

  .left-column {
    flex: 0 0 540px;
    width: 540px;
    min-width: 540px;
    min-height: 0;
    height: 700px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .portrait-panel {
    flex: 1;
    min-width: 735px;
    background: #fff;
    border-radius: 4px;
    padding: 14px 14px 12px;
    height: 700px;

    .portrait-panel__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }

    .portrait-panel__title {
      color: #334b68;
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
    }

    .portrait-panel__body {
      position: relative;
      height: 620px;
    }

    .portrait-panel__side {
      position: absolute;
      right: 0;
      width: 420px;
      min-width: 394px;
    }

    .portrait-panel__figure {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
    }

    .portrait-panel__figure-image {
      width: 610px;
      display: block;
      object-fit: contain;
    }

    .portrait-panel__figure-placeholder {
      color: #9aa6b2;
      font-size: 16px;
      line-height: 24px;
    }
  }

  @media (max-width: 1670px) {
    .left-column {
      flex-basis: 100%;
      width: 100%;
      min-width: 0;
    }
  }
}

:deep(.patient-portrait-modal-wrap) {
  .ant-modal-content {
    border-radius: 6px;
    overflow: hidden;
  }

  .ant-modal-body {
    background: #f3f3f3;
    padding: 0;
  }
}
</style>
